import { useEffect, useState } from "react";
import { Link, Redirect, useRoute, useLocation } from "wouter";
import type {
  AdjustResponse,
  TenantCustomerLookupResponse,
  TenantMeResponse,
} from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";
import { AdjustBalanceForm } from "../components/AdjustBalanceForm";
import {
  CustomerAdjustmentsTab,
  CustomerConsentsTab,
  CustomerRedemptionsTab,
  CustomerTransactionsTab,
} from "../components/MerchantCustomerTabs";

/**
 * Merchant Customer detail — Phase 13 Customer 360.
 *
 * Five-tab layout:
 *   • Overview — name, balance, status, manual-adjustment form (Phase 11)
 *   • Transactions — paginated history (Phase 13)
 *   • Redemptions — voucher activity (Phase 13)
 *   • Adjustments — manual point/stamp deltas with running balance (Phase 13)
 *   • Consents — tenant-scoped consent log (Phase 13)
 *
 * Status-aware rules (DECISIONS.md D36):
 *   • REVOKED — banner + hide AdjustBalanceForm; tabs still load (audit
 *     value preserved). Marketing toggles disabled by visual disable
 *     (rendered nowhere on this page yet — see settings page).
 *   • ANONYMIZED — banner + name forced to "Deleted Customer" + phone
 *     mask + hide AdjustBalanceForm. The tabs still resolve because
 *     the merchant retains audit access to historical activity.
 *
 * The active tab lives in the `?tab=` query param so deep links
 * survive bookmarks. Overview is the default.
 */

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "transactions", label: "Transactions" },
  { key: "redemptions", label: "Redemptions" },
  { key: "adjustments", label: "Adjustments" },
  { key: "consents", label: "Consents" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export function MerchantCustomerDetailPage() {
  const [, params] = useRoute("/merchant/customers/:tcId/:customerId");
  const tcId = params?.tcId;
  const customerId = params?.customerId;
  const [location, navigate] = useLocation();

  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;
  if (!tcId || !customerId) return <Redirect to="/merchant/customers" />;

  // Read ?tab= from window.location.search (wouter doesn't parse it).
  const search =
    typeof window !== "undefined" ? window.location.search : "";
  const sp = new URLSearchParams(search);
  const tab = (sp.get("tab") as TabKey) || "overview";
  const validTab: TabKey = TABS.some((t) => t.key === tab) ? tab : "overview";

  const [me, setMe] = useState<TenantMeResponse | null>(null);
  const [cust, setCust] = useState<TenantCustomerLookupResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  async function loadAll() {
    const [meR, cR] = await Promise.all([
      merchantFetch<TenantMeResponse>("/api/v1/tenant/me"),
      merchantFetch<TenantCustomerLookupResponse>(
        `/api/v1/tenant/customers/${customerId}`,
      ),
    ]);
    if (!meR.ok) {
      setErr(`${meR.code}: ${meR.message}`);
      return;
    }
    if (!cR.ok) {
      setErr(`${cR.code}: ${cR.message}`);
      return;
    }
    setMe(meR.data);
    setCust(cR.data);
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function onAdjust(
    delta: number,
    currency: "POINTS" | "STAMPS",
    reason: string,
  ) {
    setBusy(true);
    try {
      const r = await merchantFetch<AdjustResponse>(
        `/api/v1/tenant/customers/${tcId}/adjust`,
        {
          method: "POST",
          body: JSON.stringify({ delta, currency, reason }),
        },
      );
      if (!r.ok) {
        throw new Error(`${r.code}: ${r.message}`);
      }
      setActionMsg(
        `${delta > 0 ? "+" : ""}${delta} ${currency.toLowerCase()} applied.`,
      );
      await loadAll();
    } finally {
      setBusy(false);
    }
  }

  function selectTab(next: TabKey) {
    const base = `/merchant/customers/${tcId}/${customerId}`;
    navigate(next === "overview" ? base : `${base}?tab=${next}`);
  }
  void location;

  if (err) {
    return (
      <div className="min-h-screen pb-24">
        <div className="mx-auto max-w-2xl px-6 pt-8">
          <MerchantNav current="customers" />
          <div className="mt-6 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
          <Link
            href="/merchant/customers"
            className="mt-4 inline-block text-sm text-navy underline"
          >
            ← Back to customers
          </Link>
        </div>
      </div>
    );
  }

  if (!cust || !me) {
    return (
      <div className="min-h-screen pb-24">
        <div className="mx-auto max-w-2xl px-6 pt-8">
          <MerchantNav current="customers" />
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        </div>
      </div>
    );
  }

  // Phase 12 — anonymized customer rows arrive with name literally
  // set to "Deleted Customer" by the sweep job. We detect that
  // sentinel value to drive the banner + masked-phone display.
  const isAnonymized = cust.name === "Deleted Customer";
  const isRevoked = cust.status === "REVOKED";

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <MerchantNav current="customers" />

        <Link
          href="/merchant/customers"
          className="mt-4 inline-block text-sm text-navy underline"
        >
          ← Back
        </Link>

        {isAnonymized && (
          <div className="mt-3 rounded-card border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <p className="font-semibold">This customer has been deleted.</p>
            <p className="mt-1 text-xs">
              Personal details were anonymized at the customer's request.
              Historical activity is preserved for your records.
            </p>
          </div>
        )}
        {!isAnonymized && isRevoked && (
          <div className="mt-3 rounded-card border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="font-semibold">
              This customer has disconnected from your loyalty program.
            </p>
            <p className="mt-1 text-xs">
              They can re-enroll any time. Manual adjustments are disabled
              while disconnected; their history stays with you.
            </p>
          </div>
        )}

        <header className="mt-3">
          <h1 className="text-2xl font-bold text-navy">{cust.name}</h1>
          <p className="text-xs text-ink-dark">
            Joined {new Date(cust.joinedAt).toLocaleDateString()} · Status{" "}
            <span className="font-semibold">
              {isAnonymized ? "DELETED" : cust.status}
            </span>
          </p>
        </header>

        {actionMsg && (
          <div className="mt-4 rounded-card border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {actionMsg}
          </div>
        )}

        {/* Tab nav */}
        <nav className="mt-5 flex gap-x-4 gap-y-1 overflow-x-auto border-b border-navy/10 text-sm">
          {TABS.map((t) => {
            const active = t.key === validTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`-mb-px whitespace-nowrap border-b-2 pb-2 ${
                  active
                    ? "border-navy font-semibold text-navy"
                    : "border-transparent text-ink-dark hover:text-navy"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="mt-5">
          {validTab === "overview" && (
            <OverviewTab
              cust={cust}
              me={me}
              onAdjust={onAdjust}
              busy={busy}
              hideAdjust={isAnonymized || isRevoked}
            />
          )}
          {validTab === "transactions" && (
            <CustomerTransactionsTab tcId={tcId} />
          )}
          {validTab === "redemptions" && (
            <CustomerRedemptionsTab tcId={tcId} />
          )}
          {validTab === "adjustments" && (
            <CustomerAdjustmentsTab tcId={tcId} />
          )}
          {validTab === "consents" && <CustomerConsentsTab tcId={tcId} />}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({
  cust,
  me,
  onAdjust,
  busy,
  hideAdjust,
}: {
  cust: TenantCustomerLookupResponse;
  me: TenantMeResponse;
  onAdjust: (
    delta: number,
    currency: "POINTS" | "STAMPS",
    reason: string,
  ) => Promise<void>;
  busy: boolean;
  hideAdjust: boolean;
}) {
  return (
    <>
      <section className="card">
        <h2 className="text-sm font-semibold text-navy">Balance</h2>
        <dl className="mt-2 grid grid-cols-2 gap-y-2 text-xs text-ink-dark">
          <dt>Points</dt>
          <dd className="font-mono text-navy">{cust.pointsBalance.toFixed(2)}</dd>
          <dt>Stamps</dt>
          <dd className="font-mono text-navy">{cust.stampsCount}</dd>
          <dt>Total visits</dt>
          <dd className="font-mono">{cust.totalVisits}</dd>
          <dt>Total spend</dt>
          <dd className="font-mono">₱{cust.totalSpend}</dd>
          {cust.lastVisitAt && (
            <>
              <dt>Last visit</dt>
              <dd>{new Date(cust.lastVisitAt).toLocaleString()}</dd>
            </>
          )}
        </dl>
      </section>

      {!hideAdjust && (
        <section className="mt-5 card">
          <h2 className="text-sm font-semibold text-navy">
            Manual adjustment
          </h2>
          <p className="mt-1 text-xs text-ink-dark">
            Use sparingly — every adjustment is audited.
          </p>
          <div className="mt-3">
            <AdjustBalanceForm
              loyaltyMode={me.loyaltyMode}
              pointsBalance={cust.pointsBalance}
              stampsCount={cust.stampsCount}
              onSubmit={onAdjust}
              busy={busy}
            />
          </div>
        </section>
      )}
    </>
  );
}
